import LoadingSpinner from 'component/LoadingSpinner';
import ImageWithLoading from 'component/ImageWithLoading';
import { Option } from 'domain/model/Option';
import { DashboardDataDecisionReq } from 'domain/payload/request/dashboard-data/DashboardDataDecisionReq';
import { DashboardDetailResp, TimeTrackingPage, ActivityHistory, ResultConsentResp, ValidationStatus, ValidationDetail } from 'domain/payload/response/dashboard/DashboardDetailResp';
import { DashboardDetailResultResp, ValueHistory } from 'domain/payload/response/dashboard/DashboardDetailResultResp';
import { hasDashboardAccess, hasProjectAccess, hasAdminTransmoraAccess } from 'helpers/hasPrivilege';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { useDispatch, useSelector } from 'react-redux';
import DashboardDataService from 'service/DashboardDataService';
import { useDropzone } from "react-dropzone";
import { DashboardConfigQcResp } from 'domain/payload/response/dashboard/DashboardConfigQcResp';
import MultiSelectDropdown from 'component/MultiSelectDropdown';
import Map, { Marker, Popup, Source, Layer, MapRef } from 'react-map-gl/mapbox'
import { MAPBOX_TOKEN } from 'config/Mapbox';
import { formatElapsedTime, formatTimerUI } from 'helpers/FormatUtil';
import { RetailPartnerDetailFullDto } from 'domain/payload/response/retailPartner/RetailPartnerResp';
import { useNavPillsIndicator } from 'hooks/useNavPillsIndicator';
import { getCroppedImg } from 'utils/cropImage';

interface DashboardDetailModalProps {
  item: any;
  onClose: (decisionSuccess: boolean) => void;
}

const DashboardDetailModal: React.FC<DashboardDetailModalProps> = ({ item, onClose }) => {
  const { profile } = useSelector((state: any) => state.auth);
  const { qcDurations } = useSelector((state: any) => state.resultData);

  const dispatch = useDispatch();

  const secondsElapsed = useRef(0);
  const currentIdRef = useRef(item.id);
  const timerRef = useRef<any>(null);

  const [id, setId] = useState("");
  const [taskName, setTaskName] = useState("");
  const [description, setDescription] = useState("");
  const [code, setCode] = useState("");
  const [installerName, setInstallerName] = useState("");
  const [installerPhone, setInstallerPhone] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [projectName, setProjectName] = useState("");
  const [placeName, setPlaceName] = useState("");
  const [submittedAt, setSubmittedAt] = useState("");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [address, setAddress] = useState<string>("");
  const [status, setStatus] = useState<Option | null>(null);
  const [updatedBy, setUpdatedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [merchantListed, setMerchantListed] = useState(false);
  const [merchantPartnerDetail, setMerchantPartnerDetail] = useState<RetailPartnerDetailFullDto | null>(null);
  const [galleryFilter, setGalleryFilter] = useState("All");
  const [results, setResults] = useState<DashboardDetailResultResp[]>([]);
  const [activeSeconds, setActiveSeconds] = useState(0);
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(null);
  const [validationDetails, setValidationDetails] = useState<ValidationDetail[]>([]);
  const [activityHistory, setActivityHistory] = useState<ActivityHistory[]>([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFieldHistory, setSelectedFieldHistory] = useState<{ label: string; history: ValueHistory[]; fieldType?: Option } | null>(null);

  const [showUpdateLocation, setShowUpdateLocation] = useState(false);

  const [oldLat, setOldLat] = useState<number | null>(null);
  const [oldLng, setOldLng] = useState<number | null>(null);
  const [oldAddress, setOldAddress] = useState<string>("");

  const [newLat, setNewLat] = useState<number | null>(null);
  const [newLng, setNewLng] = useState<number | null>(null);
  const [newAddress, setNewAddress] = useState<string>("");

  const [distanceGap, setDistanceGap] = useState<number>(0);

  const [approvalRate, setApprovalRate] = useState<number | null>(null);
  const [countApprove, setCountApprove] = useState<number | null>(null);
  const [countReject, setCountReject] = useState<number | null>(null);
  const [groupName, setGroupName] = useState<string>("");
  const [groupDescription, setGroupDescription] = useState<string | null>(null);

  const [decision, setDecision] = useState("");

  const [selectedResultValue, setSelectedResultValue] = useState<DashboardDetailResultResp | null>(null)
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [isEmptyField, setIsEmptyField] = useState(false);

  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isLoadingDecision, setIsLoadingDecision] = useState(false);
  const [isLoadingUpdateValue, setIsLoadingUpdateValue] = useState(false);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showUpdateValueModal, setShowUpdateValueModal] = useState(false);
  const [showUpdateFile, setShowUpdateFile] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [isNoteReadOnly, setIsNoteReadOnly] = useState(true);
  const [noteInput, setNoteInput] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [rejectNotes, setRejectNotes] = useState("");
  const [approveNotes, setApproveNotes] = useState("");
  const [enableApproveNotes, setEnableApproveNotes] = useState(false);
  const [isLoadingUpdateNote, setIsLoadingUpdateNote] = useState(false);
  const [deletingImage, setDeletingImage] = useState<string>("");

  // Cropper state
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropAspect, setCropAspect] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [enableCrop, setEnableCrop] = useState<boolean>(true);

  const [elapsedTime, setElapsedTime] = useState<number>(item?.elapsedTime ?? 0);
  const [firstTrackingTime, setFirstTrackingTime] = useState<string>("");
  const [lastTrackingTime, setLastTrackingTime] = useState<string>("");
  const [timeTrackingPages, setTimeTrackingPages] = useState<TimeTrackingPage[]>([]);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);

  const [additionalInfo, setAdditionalInfo] = useState<any>(null);
  const [mapSectionHeight, setMapSectionHeight] = useState<number | undefined>(undefined);
  const mapSectionResizeObserverRef = useRef<ResizeObserver | null>(null);

  const mapSectionCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (mapSectionResizeObserverRef.current) {
      mapSectionResizeObserverRef.current.disconnect();
      mapSectionResizeObserverRef.current = null;
    }
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height;
          if (height > 0) {
            setMapSectionHeight(Math.round(height));
          }
        }
      });
      observer.observe(node);
      mapSectionResizeObserverRef.current = observer;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (mapSectionResizeObserverRef.current) {
        mapSectionResizeObserverRef.current.disconnect();
        mapSectionResizeObserverRef.current = null;
      }
    };
  }, []);

  const parsedAdditionalInfo = useMemo(() => {
    if (!additionalInfo) return null;
    let parsed = additionalInfo;
    if (typeof additionalInfo === 'string') {
      try {
        parsed = JSON.parse(additionalInfo);
      } catch {
        return additionalInfo;
      }
    }
    return parsed;
  }, [additionalInfo]);

  const additionalInfoItemCount = useMemo(() => {
    if (!parsedAdditionalInfo) return 0;
    if (typeof parsedAdditionalInfo === 'object') {
      if (Array.isArray(parsedAdditionalInfo)) return parsedAdditionalInfo.length;
      return Object.keys(parsedAdditionalInfo).length;
    }
    return 1;
  }, [parsedAdditionalInfo]);

  const renderAdditionalInfoContent = (info: any) => {
    if (!info) {
      return (
        <div className="text-center text-muted small py-4 fst-italic">
          Tidak ada data tambahan
        </div>
      );
    }

    let parsed = info;
    if (typeof info === 'string') {
      try {
        parsed = JSON.parse(info);
      } catch {
        parsed = info;
      }
    }

    if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed !== null) {
      const entries = Object.entries(parsed);
      if (entries.length === 0) {
        return (
          <div className="text-center text-muted small py-4 fst-italic">
            Tidak ada data tambahan
          </div>
        );
      }
      return (
        <div className="d-flex flex-column gap-2">
          {entries.map(([key, val]) => (
            <div
              key={key}
              className="p-2 rounded-2"
              style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #F1F5F9',
                transition: 'all 0.15s ease',
              }}
            >
              <div
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  color: '#64748B',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  marginBottom: '2px',
                }}
              >
                {key}
              </div>
              <div
                style={{
                  fontSize: '0.825rem',
                  fontWeight: 600,
                  color: '#0F172A',
                  wordBreak: 'break-word',
                  lineHeight: '1.3',
                }}
              >
                {typeof val === 'object' ? JSON.stringify(val) : String(val ?? '-')}
              </div>
            </div>
          ))}
        </div>
      );
    } else if (Array.isArray(parsed)) {
      if (parsed.length === 0) {
        return (
          <div className="text-center text-muted small py-4 fst-italic">
            Tidak ada data tambahan
          </div>
        );
      }
      return (
        <div className="d-flex flex-column gap-2">
          {parsed.map((item, idx) => (
            <div
              key={idx}
              className="p-2 rounded-2"
              style={{
                backgroundColor: '#F8FAFC',
                border: '1px solid #F1F5F9',
                fontSize: '0.825rem',
                fontWeight: 600,
                color: '#0F172A',
                wordBreak: 'break-word',
              }}
            >
              {typeof item === 'object' ? JSON.stringify(item) : String(item)}
            </div>
          ))}
        </div>
      );
    }
    return (
      <div
        className="p-2 rounded-2"
        style={{
          backgroundColor: '#F8FAFC',
          border: '1px solid #F1F5F9',
          fontSize: '0.825rem',
          fontWeight: 600,
          color: '#0F172A',
          wordBreak: 'break-word',
        }}
      >
        {String(parsed)}
      </div>
    );
  };

  const [resultConsents, setResultConsents] = useState<ResultConsentResp[]>([]);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showMissRoutingModal, setShowMissRoutingModal] = useState(false);
  const [selectedConsent, setSelectedConsent] = useState<ResultConsentResp | null>(null);

  const canQc = hasDashboardAccess(profile, "qc_approve");

  const dashboardDataService = DashboardDataService();

  type SimilarViewMode = "browse" | "detail";
  const [similarViewMode, setSimilarViewMode] = useState<SimilarViewMode>("browse");
  const [selectedSimilar, setSelectedSimilar] = useState<any | null>(null);
  const [retailPartnerRegistered, setRetailPartnerRegistered] = useState<boolean>(false);
  const [hasSimilarity, setHasSimilarity] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<"map" | "list">("map");
  const [mediaTab, setMediaTab] = useState<"image" | "nearby" | "merchant" | "changes" | "audio">("image");
  const [similarResults, setSimilarResults] = useState<any[]>([]);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [qcNameList, setQcNameList] = useState<DashboardConfigQcResp[]>([])
  const [enableQcSelection, setEnableQcSelection] = useState(false);
  const [selectedQc, setSelectedQc] = useState<string>("");
  const similarMapRef = useRef<MapRef | null>(null)
  const locationMapRef = useRef<MapRef | null>(null)

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const { navRef: mediaNavRef, indicatorStyle: mediaIndicatorStyle } = useNavPillsIndicator(mediaTab);
  const { navRef: viewModeNavRef, indicatorStyle: viewModeIndicatorStyle } = useNavPillsIndicator(viewMode);

  const getSimilarMarkerUrl = (sStatus: { key?: number; value?: string } | null | undefined) => {
    if (!sStatus) return "/assets/markers/icon-marker-selected.svg";
    if (sStatus.key === 4) return "/assets/markers/icon-marker-green.svg";
    if (sStatus.key === 5) return "/assets/markers/icon-marker-red.svg";
    if (sStatus.key === 3) return "/assets/markers/icon-marker-cyan.svg";
    if (sStatus.key === 8) return "/assets/markers/icon-marker-grey.svg";

    const val = (sStatus.value || "").toLowerCase();
    if (val.includes("approve") || val.includes("setuju") || val.includes("done") || val.includes("selesai") || val.includes("success")) {
      return "/assets/markers/icon-marker-green.svg";
    }
    if (val.includes("reject") || val.includes("tolak") || val.includes("fail") || val.includes("batal")) {
      return "/assets/markers/icon-marker-red.svg";
    }
    if (val.includes("progress") || val.includes("proses")) {
      return "/assets/markers/icon-marker-blue.svg";
    }
    if (val.includes("pending") || val.includes("tunggu")) {
      return "/assets/markers/icon-marker-orange.svg";
    }
    if (val.includes("archived") || val.includes("arsip")) {
      return "/assets/markers/icon-marker-grey.svg";
    }
    return "/assets/markers/icon-marker-selected.svg";
  };

  const imageResults = results.filter((r) => r.fieldType?.key === 3);
  const textResults = results.filter((r) => r.fieldType?.key !== 3);
  const hasImageResults = imageResults.length > 0;
  const hasNearbyResults = similarResults.length > 0;
  const hasMerchantResults = merchantListed;
  const hasChangesHistory = activityHistory.length > 0;
  const hasAudio = audioUrl !== null && audioUrl !== "";
  const showMediaTabs = [hasImageResults, hasNearbyResults, hasMerchantResults, hasChangesHistory, hasAudio].filter(Boolean).length > 1;
  const canOverrideMissRouting = hasDashboardAccess(profile, "qc_override");

  const [localIsRestrictMissRouting, setLocalIsRestrictMissRouting] = useState<boolean>(item?.isRestrictMissRouting ?? true);
  const [isUpdatingRestriction, setIsUpdatingRestriction] = useState<boolean>(false);

  useEffect(() => {
    if (item?.isRestrictMissRouting !== undefined) {
      setLocalIsRestrictMissRouting(item.isRestrictMissRouting);
    }
  }, [item?.isRestrictMissRouting]);

  const isRestrictMissRouting = localIsRestrictMissRouting;

  const handleToggleMissRoutingRestriction = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const token = profile?.data?.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const isOverrideChecked = e.target.checked;
    const newRestrictValue = !isOverrideChecked;

    setIsUpdatingRestriction(true);
    try {
      await dashboardDataService.updateMissRoutingRestriction(token, id, newRestrictValue);
      setLocalIsRestrictMissRouting(newRestrictValue);
      await fetchDetail();
    } catch (err: any) {
      console.error(err);
      window.alert(`Gagal memperbarui status miss routing restriction: ${err}`);
    } finally {
      setIsUpdatingRestriction(false);
    }
  };

  const isValidationFailed = validationStatus && !validationStatus.isValid;
  const failedValidationCount = validationStatus?.failedRules?.length || 0;

  const failedValidationNames = validationDetails.filter(d => !d.isPassed).map(d => d.ruleName);
  const actionRuleValidationDetails = validationDetails.filter(d =>
    !d.isPassed && (
      d.ruleType === 'Halaman Tidak Tampil' ||
      d.ruleType === 'Field Tersembunyi' ||
      d.ruleType === 'Form Berhenti' ||
      d.ruleType === 'Field Kosong'
    )
  );

  const nonMissRoutingValidationDetails = validationDetails.filter(d =>
    !d.isPassed && !(
      d.ruleType === 'Halaman Tidak Tampil' ||
      d.ruleType === 'Field Tersembunyi' ||
      d.ruleType === 'Form Berhenti' ||
      d.ruleType === 'Field Kosong'
    )
  );

  const isApproveBlockedByValidation = isRestrictMissRouting
    ? isValidationFailed
    : (nonMissRoutingValidationDetails.length > 0);

  const groupResultsByPage = (results: DashboardDetailResultResp[]) => {
    const grouped: {
      [pageId: string]: {
        pageName: string;
        pageSort: number;
        groupSort: number;
        pageColor: string;
        results: DashboardDetailResultResp[];
      };
    } = {};

    results.forEach((result) => {
      const pageKey = result.pageId || 'uncategorized';
      if (!grouped[pageKey]) {
        grouped[pageKey] = {
          pageName: result.pageName || 'Uncategorized',
          pageSort: result.pageSort || 0,
          groupSort: result.groupSort || 0,
          pageColor: result.pageColor || '#cccccc',
          results: [],
        };
      }
      grouped[pageKey].results.push(result);
    });

    return Object.entries(grouped).sort((a, b) => {
      const groupA = a[1].groupSort ?? 0;
      const groupB = b[1].groupSort ?? 0;
      if (groupA !== groupB) {
        return groupA - groupB;
      }
      const pageA = a[1].pageSort ?? 0;
      const pageB = b[1].pageSort ?? 0;
      return pageA - pageB;
    });
  };

  const groupedTextResults = groupResultsByPage(textResults);

  const getFieldHistory = (fieldLabel: string): { history: ValueHistory[]; fieldType?: Option } => {
    const result = results.find(r => r.label === fieldLabel);
    if (result && result.valueHistory && result.valueHistory.length > 0) {
      return {
        history: result.valueHistory,
        fieldType: result.fieldType
      };
    }
    return { history: [] };
  };

  const handleShowHistory = (label: string) => {
    const { history, fieldType } = getFieldHistory(label);
    if (history.length > 0) {
      setSelectedFieldHistory({ label, history, fieldType });
      setShowHistoryModal(true);
    } else {
      window.alert(`Tidak ada riwayat perubahan untuk field "${label}"`);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": []
    },
    multiple: false
  });

  const handleSingleSelectionChange = (value: string) => {
    if (selectedResultValue) {
      setSelectedResultValue({ ...selectedResultValue, value: value });
    }
  };

  const handleMultipleSelectionChange = (selectedCodes: string[]) => {
    if (selectedResultValue) {
      setSelectedOptions(selectedCodes);
      setSelectedResultValue({
        ...selectedResultValue,
        value: selectedCodes.join(', ')
      });
    }
  };

  const fetchDetail = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDetail(true);
    try {
      const res = await dashboardDataService.dashboardDataDetail(token, item.id ?? "0");
      setId(res.data?.id!)
      setTaskName(res.data?.taskName!);
      setDescription(res.data?.description!);
      setCode(res.data?.code!);
      setInstallerName(res.data?.installerName!);
      setInstallerPhone(res.data?.installerPhone!);
      setOwnerName(res.data?.ownerName!);
      setOwnerPhone(res.data?.ownerPhone!);
      setProjectName(res.data?.projectName!);
      setPlaceName(res.data?.placeName!);
      setSubmittedAt(res.data?.submittedAt!);
      setUpdatedBy(res.data?.updatedBy!);
      setLatitude(res.data?.latitude!);
      setLongitude(res.data?.longitude!);
      setAddress(res.data?.address ?? "");
      setStatus(res.data?.status!)
      setNotes(res.data?.notes ?? "");
      setMerchantListed(res.data?.merchantPartnerListed ?? false);
      setMerchantPartnerDetail(res.data?.merchantPartnerDetail ?? null);
      setActivityHistory(res.data?.activityHistory ?? []);
      setApprovalRate(res.data?.approvalRate ?? null);
      setCountApprove(res.data?.countApprove ?? null);
      setCountReject(res.data?.countReject ?? null);
      setGroupName(res.data?.groupName ?? "");
      setGroupDescription(res.data?.groupDescription ?? null);

      const parsedResults = (res.data?.results || [])
        .map((r: any) => ({
          ...r,
          options: r.options || [],
          valueHistory: r.valueHistory || []
        }))
        .sort((a: any, b: any) => {
          const groupA = a.groupSort ?? 0;
          const groupB = b.groupSort ?? 0;
          if (groupA !== groupB) return groupA - groupB;

          const pageA = a.pageSort ?? 0;
          const pageB = b.pageSort ?? 0;
          return pageA - pageB;
        });
      setResults(parsedResults);

      setSimilarResults(res.data?.similiarResults ?? []);
      setRetailPartnerRegistered(res.data?.retailPartnerRegistered ?? false);
      setHasSimilarity(!!(res.data?.similiarResults && res.data.similiarResults.length > 0));

      setResultConsents(res.data?.resultConsents ?? []);

      setElapsedTime(res.data?.elapsedTime ?? item?.elapsedTime ?? 0);
      setFirstTrackingTime(res.data?.firstTrackingTime ?? "");
      setLastTrackingTime(res.data?.lastTrackingTime ?? "");
      setTimeTrackingPages(res.data?.timeTrackingPages ?? []);

      setValidationStatus(res.data?.validationStatus ?? null);
      setValidationDetails(res.data?.validationDetails ?? []);

      setAudioUrl(res.data?.audioUrl ?? null);
      setAdditionalInfo(res.data?.additionalInfo ?? null);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengambil detail data';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDecision = async (decision: string, notes: string, qcFieldId?: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    const isBlocked = (decision === 'approve' || decision === 'approveToQcField')
      ? isApproveBlockedByValidation
      : isValidationFailed;

    if (isBlocked) {
      window.alert(`Data ini belum bisa ${decision === 'approve' ? 'disetujui' : 'ditolak'} karena masih ada validasi yang belum terpenuhi.\n\n${validationStatus?.errorMessage || 'Silakan periksa kembali datanya ya.'}`);
      return;
    }

    setIsLoadingDecision(true);
    try {
      const request: DashboardDataDecisionReq = {
        resultId: id,
        decision: decision,
        notes: notes,
        qcDuration: activeSeconds,
        qcFieldId: qcFieldId || ""
      };

      setDecision(decision)
      await dashboardDataService.dashboardDataDecision(token, request);
      window.alert(`Data berhasil ${decision === 'approve' ? 'disetujui' : decision === 'reject' ? 'ditolak' : 'diproses'}`);
      onClose(true)
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || `Terjadi kesalahan saat ${decision === 'approve' ? 'menyetujui' : decision === 'reject' ? 'menolak' : 'memproses'} data`;
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDecision(false);
    }
  };

  const handleBackToQc = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingDecision(true);
    setDecision("back_to_qc");
    try {
      await dashboardDataService.dashboardDataBackToQc(token, id);
      await fetchDetail();
      window.alert('Data berhasil dikembalikan ke QC');
      onClose(true);
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat mengembalikan data ke QC';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingDecision(false);
    }
  };

  const handleShowUpdateValue = (resultValue: DashboardDetailResultResp) => {
    setShowUpdateValueModal(true);
    setSelectedResultValue(resultValue);
    setIsEmptyField(false);

    const fieldTypeKey = resultValue.fieldType?.key ?? 0;

    if (fieldTypeKey === 6 && resultValue.value) {
      if (resultValue.value.includes(',')) {
        setSelectedOptions(resultValue.value.split(',').map(v => v.trim()));
      } else if (resultValue.value) {
        setSelectedOptions([resultValue.value]);
      } else {
        setSelectedOptions([]);
      }
    } else {
      setSelectedOptions([]);
    }
  };

  const getRatioValue = (ratio: string): number => {
    switch (ratio.trim().toLowerCase()) {
      case 'portrait':
      case 'portrait916': return 9 / 16;
      case 'portrait34': return 3 / 4;
      case 'landscape':
      case 'landscape169': return 16 / 9;
      case 'landscape43': return 4 / 3;
      case 'square':
      default: return 1;
    }
  };

  const handleShowUpdateFile = (resultValue: DashboardDetailResultResp) => {
    setShowUpdateFile(true)
    setSelectedResultValue(resultValue)
    setSelectedFile(null)
    setPreviewUrl(null)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
    setEnableCrop(true)

    if (resultValue.availableRatioCameraInApps) {
      const ratios = resultValue.availableRatioCameraInApps.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
      if (ratios.length > 0) {
        setCropAspect(getRatioValue(ratios[0]));
      }
    }
  }

  const handleSubmitUpdateFile = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    if (!selectedResultValue) {
      window.alert('Data result tidak ditemukan');
      return;
    }

    if (!selectedFile || !previewUrl) {
      window.alert('Silakan pilih file terlebih dahulu');
      return;
    }

    setIsLoadingUpdateValue(true);
    try {
      let fileToUpload: File = selectedFile;

      const hasCropRatio = Boolean(
        selectedResultValue.availableRatioCameraInApps &&
        selectedResultValue.availableRatioCameraInApps.trim() !== ''
      );

      if (hasCropRatio && enableCrop && croppedAreaPixels) {
        const croppedBlob = await getCroppedImg(previewUrl, croppedAreaPixels);
        fileToUpload = new File(
          [croppedBlob],
          selectedFile.name || 'cropped.jpg',
          { type: croppedBlob.type }
        );
      }

      await dashboardDataService.uploadFile(
        token,
        id,
        selectedResultValue.fieldId,
        fileToUpload
      );

      setShowUpdateFile(false);
      setSelectedFile(null);
      setPreviewUrl(null);
      setCroppedAreaPixels(null);
      setEnableCrop(true);
      window.alert('File berhasil diupload');
      fetchDetail();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat upload file';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingUpdateValue(false);
    }
  };

  const handleDeleteImage = async (resultValue: DashboardDetailResultResp, imageUrl: string) => {
    const confirmDelete = window.confirm('Ingin hapus gambar ini?');
    if (!confirmDelete) return;

    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setDeletingImage(imageUrl);
    setIsLoadingUpdateValue(true);
    try {
      const existing = resultValue.value || '';
      const parts = existing.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
      const filtered = parts.filter((u) => u !== imageUrl);
      const newValue = filtered.join(', ');

      await dashboardDataService.dashboardDataUpdateValue(token, resultValue.id, newValue);
      window.alert('Gambar berhasil dihapus!');
      fetchDetail();
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat menghapus gambar';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setDeletingImage("");
      setIsLoadingUpdateValue(false);
    }
  }

  const handleSubmitUpdateValue = async (resultValueId: string, value: string, resultId?: string, fieldId?: string) => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingUpdateValue(true);
    try {
      await dashboardDataService.dashboardDataUpdateValue(token, resultValueId, value, resultId, fieldId);
      setShowUpdateValueModal(false);
      setIsEmptyField(false);
      window.alert('Nilai berhasil diupdate');
      fetchDetail()
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message || err.message || 'Terjadi kesalahan saat update nilai';
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingUpdateValue(false);
    }
  }

  const isDoneStatus = status?.key === 3 || status?.value?.toLowerCase() === "done";
  const canEditNote = canQc && isDoneStatus;

  const handleOpenNoteModal = () => {
    setIsNoteReadOnly(!canEditNote);
    setNoteInput(notes || '');
    setShowNoteModal(true);
  };

  const handleSubmitUpdateNote = async () => {
    const token = profile?.data.token;
    if (!token) {
      window.alert('Token tidak ditemukan. Silakan login kembali.');
      return;
    }

    setIsLoadingUpdateNote(true);
    try {
      await dashboardDataService.dashboardDataUpdateNote(token, id, noteInput || '');
      setNotes(noteInput || '');
      setShowNoteModal(false);
      window.alert('Catatan berhasil disimpan');
    } catch (err: any) {
      console.error(err);
      const errorMessage = err.response?.data?.message;
      window.alert(`Error: ${errorMessage}`);
    } finally {
      setIsLoadingUpdateNote(false);
    }
  }

  const openGoogleMaps = async (lat: number, lng: number) => {
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank");
  }

  const handleSelectSimilar = (item: any) => {
    setSelectedSimilar(item);
    setSimilarViewMode("detail");
  };

  const handleBackToSimilar = () => {
    setSelectedSimilar(null);
    setSimilarViewMode("browse");
  };

  const calculateDistance = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371000;
    const toRad = (v: number) => (v * Math.PI) / 180;

    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const getFieldValidationStatus = (fieldId: string, fieldLabel: string) => {
    const failedValidation = validationDetails.find(d =>
      !d.isPassed && (d.ruleValue?.includes(fieldId) || d.ruleName?.toLowerCase().includes(fieldLabel?.toLowerCase()))
    );
    return failedValidation ? { hasError: true, ruleName: failedValidation.ruleName } : { hasError: false };
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  useEffect(() => {
    if (status?.key === 3) {
      const savedDuration = qcDurations[item.id] || 0;
      setActiveSeconds(savedDuration);
    } else if (status && status.key !== 3) {
      setActiveSeconds(0);
    }
    secondsElapsed.current = 0;
  }, [item.id, status?.key]);

  useEffect(() => {
    if (id) currentIdRef.current = id;
  }, [id]);

  const syncToRedux = () => {
    const finalId = currentIdRef.current;
    const durationToAdd = secondsElapsed.current;

    if (finalId && durationToAdd > 0) {
      dispatch({
        type: 'UPDATE_QC_DURATION',
        payload: {
          id: finalId,
          duration: durationToAdd
        }
      });
      secondsElapsed.current = 0;
    }
  };

  useEffect(() => {
    if (status?.key === 3) {
      timerRef.current = window.setInterval(() => {
        secondsElapsed.current += 1;
        setActiveSeconds(prev => prev + 1);

        if (secondsElapsed.current % 10 === 0) {
          syncToRedux();
        }
      }, 1000);

      const handleBeforeUnload = () => syncToRedux();
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        if (timerRef.current) window.clearInterval(timerRef.current);
        window.removeEventListener('beforeunload', handleBeforeUnload);
        syncToRedux();
      };
    } else {
      if (timerRef.current) window.clearInterval(timerRef.current);
    }
  }, [status?.key]);

  useEffect(() => {
    const finalId = currentIdRef.current;
    if (status && status.key !== 3) {
      dispatch({
        type: 'RESET_QC_DURATION',
        payload: { id: finalId }
      });
      secondsElapsed.current = 0;
      setActiveSeconds(0);
    }
  }, [status?.key]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoadingDecision && !isLoadingUpdateValue) onClose(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose, isLoadingDecision, isLoadingUpdateValue]);

  useEffect(() => {
    if (oldLat && oldLng && newLat && newLng) {
      setDistanceGap(
        Math.round(calculateDistance(oldLat, oldLng, newLat, newLng))
      );
    }
  }, [oldLat, oldLng, newLat, newLng]);

  useEffect(() => {
    if (hasImageResults) {
      setMediaTab("image");
      return;
    }
    if (hasAudio) {
      setMediaTab("audio");
      return;
    }
    if (hasNearbyResults) {
      setMediaTab("nearby");
      return;
    }
    if (hasMerchantResults) {
      setMediaTab("merchant");
      return;
    }
    if (hasChangesHistory) {
      setMediaTab("changes");
    }
  }, [hasImageResults, hasAudio, hasNearbyResults, hasMerchantResults, hasChangesHistory, item?.id]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    const dateStr = date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    const timeStr = date.toLocaleTimeString('id-ID', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return `${dateStr} ${timeStr}`;
  };

  const multiSelectOptions = selectedResultValue?.options?.map(opt => ({
    code: opt,
    name: opt
  })) || [];

  const getValidImageUrls = (value: string | null | undefined): string[] => {
    if (!value) return [];
    return value
      .split(',')
      .map(url => url.trim())
      .filter(url => url.length > 0 && url.startsWith('http'));
  };

  return (
    <>
      <div
        className="modal show d-block"
        tabIndex={-1}
        role="dialog"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          position: 'fixed',
          inset: 0,
          zIndex: 1050,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        onClick={() => {
          if (!isLoadingDecision && !isLoadingUpdateValue) onClose(false);
        }}
      >
        <div className="modal-dialog modal-lg" style={{ maxWidth: "85vw" }} role="document" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content" style={{ maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', height: '85vh', overflow: 'hidden' }}>
              {isLoadingDetail ? <LoadingSpinner /> : (
                <div className="row g-0 h-100">
                  {/* Left Column */}
                  <div className="col-md-7 h-100 pe-3" style={{ overflowY: 'auto', borderRight: '1px solid #dee2e6' }}>
                    <div className="sticky-top bg-white pt-2 pb-2 mb-2" style={{ zIndex: 10, borderBottom: '1px solid #f8f9fa' }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h5 className="mb-1">{taskName}</h5>
                          <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Result ID : {item.id}<br />
                            Submitted at {submittedAt}
                          </p>
                        </div>

                        <div className="text-end d-flex align-items-center gap-2">
                          <span className="badge rounded-pill bg-light text-dark border" style={{ fontSize: '0.85rem', fontWeight: '500' }}>
                            <i className="fa-regular fa-clock me-1 text-primary"></i>
                            {formatElapsedTime(elapsedTime)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {isValidationFailed && (
                      <p className="small fst-italic mb-2 text-danger">
                        <i className="fa-solid fa-circle-exclamation me-1"></i>
                        *Terdapat {failedValidationCount} validasi yang gagal
                      </p>
                    )}

                    {hasSimilarity && (
                      <p className="small fst-italic mb-2 text-primary">
                        <i className="fa-solid fa-circle-exclamation me-1"></i>
                        *Data memiliki kemiripan dengan data lain
                      </p>
                    )}

                    {resultConsents && resultConsents.length > 0 && (
                      <div
                        className="px-3 py-2 mb-2 rounded-2 d-flex align-items-center justify-content-between"
                        style={{
                          backgroundColor: '#EFF6FF',
                          border: '1px solid #2B75DA',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className="fa-solid fa-circle-check text-primary me-2" style={{ fontSize: '14px' }}></i>
                          <span className="fw-semibold text-dark small">Pengecekan Toko Sekitar</span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary btn-sm px-2.5 py-1 fw-medium"
                          style={{ fontSize: '12px', lineHeight: '1.2' }}
                          onClick={() => {
                            setSelectedConsent(resultConsents[0]);
                            setShowConsentModal(true);
                          }}
                        >
                          Attachment
                        </button>
                      </div>
                    )}

                    {actionRuleValidationDetails && actionRuleValidationDetails.length > 0 && (
                      <div
                        className="px-3 py-2 mb-2 rounded-2 d-flex align-items-center justify-content-between"
                        style={{
                          backgroundColor: !isRestrictMissRouting ? '#FFFBEB' : '#FEF2F2',
                          border: !isRestrictMissRouting ? '1px solid #FCD34D' : '1px solid #F87171',
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <i className={`fa-solid fa-triangle-exclamation ${!isRestrictMissRouting ? 'text-warning' : 'text-danger'} me-2`} style={{ fontSize: '14px' }}></i>
                          <div>
                            <span className={`fw-semibold ${!isRestrictMissRouting ? 'text-warning-emphasis' : 'text-danger'} small`}>Rute Pengisian Perlu Dicek</span>
                            {!isRestrictMissRouting && (
                              <span className="badge bg-success-subtle text-success ms-2" style={{ fontSize: '10px' }}>
                                Boleh Diproses
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`btn ${!isRestrictMissRouting ? 'btn-warning text-dark' : 'btn-danger'} btn-sm px-2.5 py-1 fw-medium`}
                          style={{ fontSize: '12px', lineHeight: '1.2' }}
                          onClick={() => setShowMissRoutingModal(true)}
                        >
                          Detail
                        </button>
                      </div>
                    )}

                    <div className="row g-3 mb-2">
                      {/* Section 1: Maps sampai Button Approve */}
                      <div ref={mapSectionCallbackRef} className="col-md-8 col-12 d-flex flex-column">
                        <div className="mb-2 border rounded overflow-hidden" style={{ height: '180px' }}>
                          <iframe
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            loading="lazy"
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${latitude ?? 0},${longitude ?? 0}&zoom=16`}
                          ></iframe>
                        </div>

                        <div className="mb-2 d-flex align-items-center small">
                          <i className="fa fa-map-marker-alt me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                          <a href="#" onClick={() => latitude != null && longitude != null ? openGoogleMaps(latitude, longitude) : null}>
                            {latitude}, {longitude}
                          </a>
                          <button
                            type="button"
                            className="p-0 mx-2 bg-transparent border-0 shadow-none"
                            onClick={() => {
                              setOldLat(latitude); setOldLng(longitude); setOldAddress(address);
                              setNewLat(latitude); setNewLng(longitude); setNewAddress(address);
                              setShowUpdateLocation(true);
                            }}
                          >
                            <i className="fa-solid fa-pencil ms-1 text-muted" />
                          </button>
                        </div>

                        <div className="mb-2 d-flex align-items-start small text-muted">
                          <i className="fa-solid fa-house me-2 text-muted" style={{ width: '16px', textAlign: 'center', marginTop: '2px' }}></i>
                          <div style={{ wordBreak: 'break-word' }}>
                            <span>{address || '-'}</span>
                          </div>
                        </div>

                        <div className="mb-2 small">
                          <div className="d-flex align-items-center flex-wrap mb-1">
                            <i className="fa fa-user me-2 text-muted" style={{ width: '16px', textAlign: 'center', flexShrink: 0 }}></i>
                            <span className="fw-semibold text-dark">
                              {installerName || '-'}
                              {installerPhone ? ` - ${installerPhone}` : ''}
                            </span>

                            {(approvalRate != null || countApprove != null || countReject != null) && (
                              <div className="d-inline-flex align-items-center flex-wrap ms-1">
                                <span className="text-muted mx-2">|</span>
                                <span className="text-muted">Approval Rate </span>
                                {approvalRate != null && (
                                  <span className="ms-1 fw-bold" style={{ color: '#4b5563' }}>
                                    {Math.round(approvalRate * (approvalRate <= 1 ? 100 : 1))}%
                                  </span>
                                )}
                                {(countApprove != null || countReject != null) && (
                                  <span className="ms-1">
                                    (<span className="text-success fw-bold">{(countApprove ?? 0).toLocaleString()}</span>
                                    <span className="text-muted"> / </span>
                                    <span className="text-danger fw-bold">{(countReject ?? 0).toLocaleString()}</span>)
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {ownerName && (
                            <div className="d-flex align-items-center mb-1">
                              <i className="fa fa-store me-2 text-muted" style={{ width: '16px', textAlign: 'center' }}></i>
                              {ownerName} - {ownerPhone}
                            </div>
                          )}
                        </div>

                        <div className="d-flex align-items-center flex-wrap gap-2 mb-2">
                          <div className={`badge-status bg-${status?.value?.toLowerCase() || item.status?.value?.toLowerCase()}`}>
                            {status?.value || item.status?.value}
                          </div>

                          {updatedBy && (
                            <>
                              <p className="mb-0 small text-muted">
                                by {updatedBy}
                              </p>
                              <span className="text-muted small">•</span>
                            </>
                          )}

                          <p className="mb-0 fst-italic text-muted small">
                            {!notes ? '*-' : `*${notes}`}
                          </p>
                          <button type="button" className="p-0 bg-transparent border-0 shadow-none" onClick={handleOpenNoteModal}>
                            <i className={`fa-solid ${canEditNote ? 'fa-pencil' : 'fa-eye'} small text-muted`} />
                          </button>
                        </div>
                      </div>

                      {/* Section 2: Konten Additional Info */}
                      <div
                        className="col-md-4 col-12 d-flex flex-column"
                        style={{
                          maxHeight: mapSectionHeight ? `${mapSectionHeight}px` : undefined,
                          height: mapSectionHeight ? `${mapSectionHeight}px` : 'auto',
                        }}
                      >
                        <div
                          className="border rounded-3 p-3 bg-white h-100 d-flex flex-column"
                          style={{
                            borderColor: '#e2e8f0',
                            minHeight: 0,
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2 flex-shrink-0">
                            <div className="d-flex align-items-center gap-2">
                              <i className="fa-solid fa-sliders text-primary" style={{ fontSize: '0.85rem' }}></i>
                              <span className="fw-bold text-dark" style={{ fontSize: '0.85rem' }}>Additional Info</span>
                            </div>
                            {additionalInfoItemCount > 0 && (
                              <span
                                className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle"
                                style={{ fontSize: '0.7rem', fontWeight: 600 }}
                              >
                                {additionalInfoItemCount} item
                              </span>
                            )}
                          </div>
                          <div
                            className="flex-shrink-0"
                            style={{
                              height: '2px',
                              background: 'linear-gradient(90deg, #6366F1 0%, #A855F7 100%)',
                              borderRadius: '2px',
                              marginBottom: '10px',
                            }}
                          />
                          <div
                            className="flex-grow-1 overflow-y-auto pe-1"
                            style={{ minHeight: 0 }}
                          >
                            {renderAdditionalInfoContent(parsedAdditionalInfo)}
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />

                    <div className="mb-1">
                      {groupedTextResults.map(([pageId, pageData]) => (
                        <div key={pageId} className="mb-2 card p-1 px-2">
                          <h6
                            className="sticky-top bg-white py-2 mb-3 d-flex align-items-center justify-content-between gap-2"
                            style={{ zIndex: 5, top: '85px', borderBottom: '1px solid #e9ecef' }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span
                                style={{
                                  display: 'inline-block',
                                  width: 12,
                                  height: 12,
                                  borderRadius: 3,
                                  backgroundColor: pageData.pageColor,
                                  flexShrink: 0,
                                }}
                              />
                              {pageData.pageName}
                            </div>
                            {(() => {
                              const trackingPage = timeTrackingPages.find(p => p.pageId === pageId);
                              if (!trackingPage || trackingPage.totalDurationSeconds === 0) return null;
                              return (
                                <span
                                  className="fw-normal text-muted"
                                  style={{ whiteSpace: 'nowrap' }}
                                >
                                  {formatElapsedTime(trackingPage.totalDurationSeconds)}
                                </span>
                              );
                            })()}
                          </h6>
                          {pageData.results.map((r) => {
                            const fieldTypeKey = r.fieldType?.key ?? 0;
                            let displayValue = r.value || '-';
                            const validationError = getFieldValidationStatus(r.fieldId, r.label);
                            const hasHistory = r.valueHistory && r.valueHistory.length > 0;

                            if (fieldTypeKey === 6 && r.value) {
                              displayValue = r.value;
                            }

                            return (
                              <div className="mb-3" key={r.id}>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className={`small fw-bold ${validationError.hasError ? 'text-danger' : 'text-primary'}`}>
                                    {r.label}
                                    {validationError.hasError && (
                                      <i className="fa-solid fa-circle-exclamation ms-1 text-danger" title={validationError.ruleName}></i>
                                    )}
                                  </span>
                                  <div className="d-flex align-items-center gap-2">
                                    {hasHistory && (
                                      <button
                                        type="button"
                                        className="p-0 bg-transparent border-0 shadow-none"
                                        onClick={() => handleShowHistory(r.label)}
                                        title="Lihat riwayat perubahan"
                                      >
                                        <i className="fa-solid fa-clock-rotate-left small text-muted" />
                                      </button>
                                    )}
                                    <button type="button" className="p-0 bg-transparent border-0 shadow-none" onClick={() => handleShowUpdateValue(r)}>
                                      <i className="fa-solid fa-pencil ms-1 text-muted" />
                                    </button>
                                  </div>
                                </div>
                                <div className={`mt-1 text-muted small d-flex justify-content-between border-bottom pb-2 ${validationError.hasError ? 'border-danger' : ''}`}>
                                  <span>{displayValue}</span>
                                </div>
                                {validationError.hasError && (
                                  <div className="text-danger small mt-1">
                                    <i className="fa-solid fa-info-circle me-1"></i>
                                    {validationError.ruleName}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-5 h-100 ps-3" style={{ overflowY: 'auto' }}>
                    {timeTrackingPages.length > 0 && (
                      <div className="mb-3 pt-4">
                        <h6 className="fw-semibold mb-2">Input Tracking</h6>
                        <div
                          className="d-flex mb-1"
                          style={{ height: 16, gap: 1, position: 'relative' }}
                          onMouseLeave={() => setTooltip(null)}
                        >
                          {(() => {
                            const total = timeTrackingPages.length;
                            const totalDuration = timeTrackingPages.reduce((sum, p) => sum + p.totalDurationSeconds, 0);
                            return timeTrackingPages.map((page, index) => {
                              const widthPct = totalDuration > 0
                                ? (page.totalDurationSeconds / totalDuration) * 100
                                : 100 / total;
                              return (
                                <div
                                  key={page.pageId}
                                  style={{
                                    width: `${widthPct}%`,
                                    backgroundColor: page.pageColor || '#3B4BE2',
                                    minWidth: 4,
                                    flexShrink: 0,
                                    cursor: 'pointer',
                                    borderRadius: 2,
                                  }}
                                  onMouseEnter={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setTooltip({
                                      x: rect.left + rect.width / 2,
                                      y: rect.top - 8,
                                      text: page.pageName,
                                    });
                                  }}
                                  onMouseLeave={() => {
                                    setTooltip(null);
                                  }}
                                />
                              );
                            });
                          })()}
                        </div>

                        {tooltip && (
                          <div
                            style={{
                              position: 'fixed',
                              left: tooltip.x,
                              top: tooltip.y,
                              transform: 'translate(-50%, -100%)',
                              background: '#fff',
                              border: '1px solid #dee2e6',
                              borderRadius: 6,
                              padding: '5px 10px',
                              fontSize: '0.75rem',
                              pointerEvents: 'none',
                              zIndex: 2000,
                              whiteSpace: 'nowrap',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            }}
                          >
                            <b style={{ display: 'block' }}>{tooltip.text}</b>
                            {formatElapsedTime(
                              timeTrackingPages.find(p => p.pageName === tooltip.text)?.totalDurationSeconds ?? 0
                            )}
                          </div>
                        )}

                        <div className="d-flex justify-content-between" style={{ fontSize: '0.7rem', color: '#888' }}>
                          <span>{firstTrackingTime ? formatDate(firstTrackingTime) : '-'}</span>
                          <span>{lastTrackingTime ? formatDate(lastTrackingTime) : '-'}</span>
                        </div>
                      </div>
                    )}

                    {/* Media Tabs Header */}
                    {showMediaTabs && (
                      <div className="w-100 mb-3 sticky-top bg-white pt-2">
                        <div ref={mediaNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                          <div
                            className="nav-pill-indicator"
                            style={mediaIndicatorStyle}
                          />
                          {hasImageResults && (
                            <div className='flex-fill'>
                              <button
                                type="button"
                                className={`w-100 nav-link rounded-0 border-0 ${mediaTab === "image" ? "active" : ""}`}
                                onClick={() => setMediaTab("image")}
                              >
                                Image Results
                              </button>
                            </div>
                          )}
                          {hasAudio && (
                            <div className='flex-fill'>
                              <button
                                type="button"
                                className={`w-100 nav-link rounded-0 border-0 ${mediaTab === "audio" ? "active" : ""}`}
                                onClick={() => setMediaTab("audio")}
                              >
                                Audio
                              </button>
                            </div>
                          )}
                          {hasNearbyResults && (
                            <div className='flex-fill'>
                              <button
                                type="button"
                                className={`w-100 nav-link rounded-0 border-0 ${mediaTab === "nearby" ? "active" : ""}`}
                                onClick={() => setMediaTab("nearby")}
                              >
                                Nearby Data ({similarResults.length})
                              </button>
                            </div>
                          )}
                          {hasMerchantResults && (
                            <div className='flex-fill'>
                              <button
                                type="button"
                                className={`w-100 nav-link rounded-0 border-0 ${mediaTab === "merchant" ? "active" : ""}`}
                                onClick={() => setMediaTab("merchant")}
                              >
                                Merchant Detail
                              </button>
                            </div>
                          )}
                          {hasChangesHistory && (
                            <div className='flex-fill'>
                              <button
                                type="button"
                                className={`w-100 nav-link rounded-0 border-0 ${mediaTab === "changes" ? "active" : ""}`}
                                onClick={() => setMediaTab("changes")}
                              >
                                Changes History
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {((showMediaTabs && mediaTab === "audio") || (!showMediaTabs && hasAudio)) && (
                      <div className="mt-3">
                        <div className="card p-3">
                          <div className="d-flex align-items-center gap-3 mb-3">
                            <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                              <i className="fa-solid fa-music text-primary" style={{ fontSize: '24px' }}></i>
                            </div>
                            <div>
                              <h6 className="mb-0 fw-bold">Audio Recording</h6>
                            </div>
                          </div>

                          {audioUrl ? (
                            <audio
                              controls
                              className="w-100"
                              style={{ borderRadius: '8px' }}
                              controlsList="nodownload"
                            >
                              <source src={audioUrl} type="audio/mpeg" />
                              <source src={audioUrl} type="audio/mp4" />
                              <source src={audioUrl} type="audio/m4a" />
                              <source src={audioUrl} type="audio/aac" />
                              Browser Anda tidak mendukung audio player.
                            </audio>
                          ) : (
                            <div className="text-center text-muted py-3">
                              <i className="fa-solid fa-circle-exclamation me-2"></i>
                              Audio tidak tersedia
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Tab Content: Image */}
                    {((showMediaTabs && mediaTab === "image") || (!showMediaTabs && hasImageResults)) && (
                      <div className="d-grid gap-3">
                        {imageResults.map((r) => {
                          const validationError = getFieldValidationStatus(r.fieldId, r.label);
                          const hasHistory = r.valueHistory && r.valueHistory.length > 0;
                          const imageUrls = getValidImageUrls(r.value);

                          return (
                            <div className="mb-3" key={r.id}>
                              <div className="d-flex align-items-center justify-content-between mb-1">
                                <div className="d-flex align-items-center">
                                  <span className={`small fw-bold ${validationError.hasError ? 'text-danger' : 'text-primary'}`}>
                                    {r.label}
                                    {validationError.hasError && (
                                      <i className="fa-solid fa-circle-exclamation ms-1 text-danger" title={validationError.ruleName}></i>
                                    )}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  {hasHistory && (
                                    <button
                                      type="button"
                                      className="p-0 bg-transparent border-0 shadow-none"
                                      onClick={() => handleShowHistory(r.label)}
                                      title="Lihat riwayat perubahan"
                                    >
                                      <i className="fa-solid fa-clock-rotate-left small text-muted" />
                                    </button>
                                  )}
                                  <button type="button" className="p-0 bg-transparent border-0 shadow-none" onClick={() => handleShowUpdateFile(r)}>
                                    <i className="fa-solid fa-pencil text-muted" />
                                  </button>
                                </div>
                              </div>

                              {imageUrls.length > 0 ? (
                                <div className="d-flex flex-column gap-2">
                                  {imageUrls.map((imgUrl, index) => (
                                    <div key={index} style={{ position: 'relative' }}>
                                      <a href={imgUrl} target="_blank" rel="noreferrer">
                                        <ImageWithLoading
                                          src={imgUrl}
                                          alt={`${r.label} - ${index + 1}`}
                                          className={`border rounded ${validationError.hasError ? 'border-danger' : ''}`}
                                          style={{ width: "100%", height: "auto", display: "block" }}
                                        />
                                      </a>

                                      <button
                                        type="button"
                                        className="text-white bg-danger border-0 shadow-none"
                                        style={{ position: 'absolute', left: 6, top: 6, zIndex: 10, padding: 0, width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                                        onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleDeleteImage(r, imgUrl); }}
                                        disabled={isLoadingUpdateValue && deletingImage === imgUrl}
                                        title="Delete image"
                                      >
                                        {isLoadingUpdateValue && deletingImage === imgUrl ? (
                                          <span className="spinner-border spinner-border-sm" style={{ width: 10, height: 10, borderWidth: 1.5 }} role="status" aria-hidden="true" />
                                        ) : (
                                          <i className="fa fa-trash" style={{ fontSize: '0.65rem', lineHeight: 1 }} />
                                        )}
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-muted small">-</div>
                              )}

                              {validationError.hasError && (
                                <div className="text-danger small mt-1">
                                  <i className="fa-solid fa-info-circle me-1"></i>
                                  {validationError.ruleName}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Tab Content: Nearby Data */}
                    {((showMediaTabs && mediaTab === "nearby") || (!showMediaTabs && hasNearbyResults)) && (
                      <>
                        <div className="mb-2 d-flex justify-content-between align-items-center">
                          <div className="gap-2 d-flex align-items-center">
                            {similarViewMode === "detail" && (
                              <button
                                className="p-0 btn btn-link"
                                onClick={handleBackToSimilar}
                              >
                                <i className="fa fa-arrow-left"></i>
                              </button>
                            )}
                            <h6 className="mb-0">
                              {similarViewMode === "browse" ? "Nearby Data" : "Detail Nearby Data"}
                            </h6>
                          </div>

                          {similarViewMode === "browse" && (
                            <div ref={viewModeNavRef} className="nav-pills-animated h-100" style={{ height: 50, maxHeight: 50 }}>
                              <div
                                className="nav-pill-indicator"
                                style={viewModeIndicatorStyle}
                              />
                              <button
                                type='button'
                                className={`h-100 nav-link py-1 px-2 ${viewMode === "map" ? "active" : ""}`}
                                onClick={() => setViewMode("map")}
                              >
                                <i className="fa-solid fa-map"></i>
                              </button>
                              <button
                                type='button'
                                className={`h-100 nav-link py-1 px-2 ${viewMode === "list" ? "active" : ""}`}
                                onClick={() => setViewMode("list")}
                              >
                                <i className="fa-solid fa-list"></i>
                              </button>
                            </div>
                          )}
                        </div>

                        {similarViewMode === "browse" && (
                          <>
                            {viewMode === "map" ? (
                              <div style={{ height: 300, borderRadius: 8, overflow: "hidden" }}>
                                {latitude !== null && longitude !== null && (
                                  <Map
                                    ref={similarMapRef}
                                    mapboxAccessToken={MAPBOX_TOKEN}
                                    initialViewState={{
                                      longitude: longitude,
                                      latitude: latitude,
                                      zoom: 17,
                                    }}
                                    style={{ width: '100%', height: '100%' }}
                                    mapStyle="mapbox://styles/mapbox/streets-v12"
                                    onClick={() => setSelectedTask(null)}
                                  >
                                    <Marker
                                      longitude={longitude}
                                      latitude={latitude}
                                      anchor="bottom"
                                    >
                                      <img
                                        src="/assets/markers/icon-marker.svg"
                                        style={{ width: 25, height: 25, cursor: 'pointer' }}
                                        onMouseEnter={() => setSelectedTask({ latitude, longitude, taskName, installerName, placeName, status })}
                                        onMouseLeave={() => setSelectedTask(null)}
                                        alt=""
                                      />
                                    </Marker>

                                    {similarResults.map((s) => (
                                      <Marker
                                        key={s.id}
                                        longitude={s.longitude}
                                        latitude={s.latitude}
                                        anchor="bottom"
                                        onClick={(e) => {
                                          e.originalEvent.stopPropagation()
                                          handleSelectSimilar(s)
                                        }}
                                      >
                                        <img
                                          src={getSimilarMarkerUrl(s.status)}
                                          style={{ width: 25, height: 25, cursor: 'pointer' }}
                                          onMouseEnter={() => setSelectedTask(s)}
                                          onMouseLeave={() => setSelectedTask(null)}
                                          alt=""
                                        />
                                      </Marker>
                                    ))}

                                    {selectedTask?.latitude && selectedTask?.longitude && (
                                      <Popup
                                        longitude={selectedTask.longitude}
                                        latitude={selectedTask.latitude}
                                        anchor="bottom"
                                        closeButton={false}
                                        closeOnClick={false}
                                        offset={30}
                                      >
                                        <div style={{ maxWidth: 200, pointerEvents: 'none' }}>
                                          <h6 className="fw-bold mb-1">
                                            {selectedTask.taskName || 'Unnamed Task'}
                                          </h6>
                                          <p className="mb-1 small">{selectedTask.installerName || '-'}</p>
                                          <p className="mb-1 small text-muted">
                                            {selectedTask.placeName || 'No address'}
                                          </p>
                                          <div className="small text-muted">
                                            <i className="fa-solid fa-location-dot me-1"></i>
                                            {selectedTask.latitude.toFixed(6)}, {selectedTask.longitude.toFixed(6)}
                                          </div>
                                        </div>
                                      </Popup>
                                    )}
                                  </Map>
                                )}
                              </div>
                            ) : (
                              <div className="mt-2 list-group">
                                {similarResults.length === 0 && (
                                  <div className="text-muted small fst-italic">
                                    No nearby data
                                  </div>
                                )}

                                {similarResults.map((s) => (
                                  <div
                                    key={s.id}
                                    className="list-group-item list-group-item-action"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleSelectSimilar(s)}
                                  >
                                    <div className="d-flex justify-content-between">
                                      <div>
                                        <div className="fw-semibold">{s.taskName}</div>
                                        <div>
                                          <small className="text-muted">
                                            Result ID: {s.id}
                                          </small>
                                        </div>
                                        <div>
                                          <small className="text-muted">
                                            {formatDate(s.submittedAt)}
                                          </small>
                                        </div>
                                        <div>
                                          <small className="text-muted">
                                            {s.placeName}
                                          </small>
                                        </div>
                                        <small className="text-muted">
                                          {s.installerName}
                                        </small>
                                      </div>
                                      <div className="text-end">
                                        <div className="mb-1">
                                          {s.status?.key === 4 ? (
                                            <span className="badge bg-success">
                                              {s.status.value}
                                            </span>
                                          ) : s.status?.key === 5 ? (
                                            <span className="badge bg-danger">
                                              {s.status.value}
                                            </span>
                                          ) : s.status?.key === 8 ? (
                                            <span className="badge bg-secondary">
                                              {s.status.value}
                                            </span>
                                          ) : s.status?.value ? (
                                            <span className="badge bg-warning text-dark">
                                              {s.status.value}
                                            </span>
                                          ) : null}
                                        </div>
                                        {s.projectName && (
                                          <div>
                                            <span className="badge bg-light text-secondary border" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                                              {s.projectName}
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <small className="mt-1 text-muted d-block">
                                      {s.latitude}, {s.longitude}
                                    </small>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}

                        {similarViewMode === "detail" && selectedSimilar && (
                          <div className="mt-2">
                            <div className='p-3 list-group' style={{
                              border: '1px solid #dee2e6',
                              borderRadius: '8px',
                              backgroundColor: '#fff'
                            }}>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <div className="fw-semibold">{selectedSimilar.taskName}</div>
                                  <div>
                                    <small className="text-muted">
                                      Result ID: {selectedSimilar.id}
                                    </small>
                                  </div>
                                  <div>
                                    <small className="text-muted">
                                      {formatDate(selectedSimilar.submittedAt)}
                                    </small>
                                  </div>
                                  <div>
                                    <small className="text-muted">
                                      {selectedSimilar.placeName}
                                    </small>
                                  </div>
                                  <small className="text-muted">
                                    {selectedSimilar.installerName}
                                  </small>
                                </div>
                                <div className="text-end">
                                  <div className="mb-1">
                                    {selectedSimilar.status?.key === 4 ? (
                                      <span className="badge bg-success">
                                        {selectedSimilar.status.value}
                                      </span>
                                    ) : selectedSimilar.status?.key === 5 ? (
                                      <span className="badge bg-danger">
                                        {selectedSimilar.status.value}
                                      </span>
                                    ) : selectedSimilar.status?.key === 8 ? (
                                      <span className="badge bg-secondary">
                                        {selectedSimilar.status.value}
                                      </span>
                                    ) : selectedSimilar.status?.value ? (
                                      <span className="badge bg-warning text-dark">
                                        {selectedSimilar.status.value}
                                      </span>
                                    ) : null}
                                  </div>
                                  {selectedSimilar.projectName && (
                                    <div>
                                      <span className="badge bg-light text-secondary border" style={{ fontSize: '0.7rem', fontWeight: '500' }}>
                                        {selectedSimilar.projectName}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <hr />
                            {selectedSimilar.results?.map((r: any) => {
                              const isImageField = r.fieldType?.key === 3;
                              const imageUrls = getValidImageUrls(r.value);

                              return (
                                <div key={r.id} className="mb-3">
                                  <span className="text-primary">{r.label}</span>
                                  <div className="mt-1 text-muted small">
                                    {isImageField ? (
                                      imageUrls.length > 0 ? (
                                        <div className="d-flex flex-column gap-2">
                                          {imageUrls.map((imgUrl, idx) => (
                                            <a key={idx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                              <ImageWithLoading
                                                src={imgUrl}
                                                alt={`${r.label} - ${idx + 1}`}
                                                className="rounded img-fluid"
                                                style={{ width: '100%', maxHeight: '300px', objectFit: 'contain' }}
                                              />
                                            </a>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-muted">-</div>
                                      )
                                    ) : (
                                      r.value || '-'
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}

                    {/* Tab Content: Merchant */}
                    {((showMediaTabs && mediaTab === "merchant") || (!showMediaTabs && hasMerchantResults)) && (
                      <div className="mt-2">
                        <div className="p-3">

                          <h6 className="fw-bold text-primary mb-1">{merchantPartnerDetail?.storeName ?? '-'}</h6>
                          <p className="text-muted small mb-3">{merchantPartnerDetail?.latitude}, {merchantPartnerDetail?.longitude}</p>

                          <hr className="my-2" />

                          <div className="row mb-3">
                            <div className="col-6">
                              <small className="text-muted d-block">Pemilik Toko</small>
                              <span className="fw-bold small">{merchantPartnerDetail?.ownerName ?? '-'}</span>
                            </div>
                            <div className="col-6">
                              <small className="text-muted d-block">Kontak</small>
                              <span className="fw-bold small">{merchantPartnerDetail?.ownerPhone ?? '-'}</span>
                            </div>
                          </div>

                          <div className="row mb-3">
                            <div className="col-4">
                              <small className="text-muted d-block">Store Type</small>
                              <span className="fw-bold small">{merchantPartnerDetail?.storeTypeName ?? '-'}</span>
                            </div>
                            <div className="col-4">
                              <small className="text-muted d-block">Road Type</small>
                              <span className="fw-bold small">{merchantPartnerDetail?.roadTypeName ?? '-'}</span>
                            </div>
                            <div className="col-4">
                              <small className="text-muted d-block">Years in Operation</small>
                              <span className="fw-bold small">{merchantPartnerDetail?.yearsInOperation ?? '-'}</span>
                            </div>
                          </div>

                          <div>
                            <small className="text-muted d-block">Address</small>
                            <span className="fw-bold small">{merchantPartnerDetail?.address || '-'}</span>
                          </div>

                        </div>
                        {merchantPartnerDetail?.galleryList && merchantPartnerDetail.galleryList.length > 0 && (
                          <>
                            <h6 className="fw-bold text-primary mb-3">Gallery</h6>

                            <div className="d-flex gap-2 mb-3 flex-wrap">
                              <button
                                onClick={() => setGalleryFilter("All")}
                                className={`btn btn-sm rounded-pill ${galleryFilter === "All" ? "btn-primary" : "btn-outline-secondary"}`}
                              >
                                All
                              </button>
                              {merchantPartnerDetail.galleryList.map((group) => (
                                <button
                                  key={group.id}
                                  onClick={() => setGalleryFilter(group.name)}
                                  className={`btn btn-sm rounded-pill ${galleryFilter === group.name ? "btn-primary" : "btn-outline-secondary"}`}
                                >
                                  {group.name}
                                </button>
                              ))}
                            </div>

                            <div className="d-flex flex-wrap gap-2" style={{ maxHeight: "500px", overflowY: "auto" }}>
                              {(() => {
                                const filteredImages = galleryFilter === "All"
                                  ? merchantPartnerDetail.galleryList.flatMap((group) =>
                                    group.items.map((item) => ({ ...item, category: group.name }))
                                  )
                                  : merchantPartnerDetail.galleryList
                                    .filter((group) => group.name === galleryFilter)
                                    .flatMap((group) =>
                                      group.items.map((item) => ({ ...item, category: group.name }))
                                    );

                                return filteredImages.length > 0 ? (
                                  filteredImages.map((img) => (
                                    <div key={img.id} style={{ width: "calc(50% - 4px)" }}>
                                      <img
                                        src={img.fileUrl}
                                        alt={img.category}
                                        className="w-100 rounded shadow-sm"
                                        style={{ height: "150px", objectFit: "cover", cursor: "pointer" }}
                                        onClick={() => window.open(img.fileUrl, "_blank")}
                                      />
                                    </div>
                                  ))
                                ) : (
                                  <div className="col-12 text-center py-4">
                                    <i className="fa-solid fa-images text-muted fa-3x mb-2"></i>
                                    <p className="text-muted">No images available</p>
                                  </div>
                                );
                              })()}
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {((showMediaTabs && mediaTab === "changes") || (!showMediaTabs && hasChangesHistory)) && (
                      <div className="mt-2" style={{ position: 'relative' }}>
                        <div style={{ position: 'relative', paddingLeft: 48 }}>
                          <div style={{
                            position: 'absolute',
                            left: 19,
                            top: 0,
                            bottom: 0,
                            width: 2,
                            backgroundColor: '#e2e8f0',
                            zIndex: 0,
                          }} />

                          {[...activityHistory].reverse().map((entry, idx) => {
                            const isImageField = entry.fieldType?.key === 3;
                            const hasFromTo = entry.oldValue != null || entry.newValue != null;
                            const isRecordCreated = entry.action?.toLowerCase().includes('created') || entry.action?.toLowerCase().includes('record');
                            const isStatusChange = entry.fieldLabel === 'Status';

                            const { date, time } = (() => {
                              const parts = (entry.changedAt || '').split(' ');
                              return { date: parts[0] || '-', time: parts[1] || '' };
                            })();

                            const statusValue = entry.newValue || entry.status?.value || '';

                            const splitImageUrls = (value: string | null | undefined): string[] => {
                              if (!value) return [];
                              return value.split(',').map(url => url.trim()).filter(url => url.length > 0);
                            };

                            const getStatusBgColor = (status: string) => {
                              const value = status?.toLowerCase() || '';
                              if (value === 'done') return '#fffbdbff';
                              if (value === 'approved') return '#dbffe0ff';
                              if (value === 'rejected') return '#ffdbdbff';
                              if (value === 'Incomplete') return '#dbe0ffff';
                              return '#e2e8f0';
                            };

                            const oldImages = splitImageUrls(entry.oldValue);
                            const newImages = splitImageUrls(entry.newValue);

                            return (
                              <div key={idx} style={{ position: 'relative', marginBottom: 0 }}>
                                <div className={`d-flex align-items-center justify-content-center ${isStatusChange ? `bg-${statusValue.toLowerCase()}` : 'bg-light'}`} style={{
                                  position: 'absolute',
                                  left: -48,
                                  top: 2,
                                  width: 36,
                                  height: 36,
                                  borderRadius: '50%',
                                  zIndex: 1,
                                  border: '2px solid #fff',
                                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                                }}>
                                  {isStatusChange ? (
                                    <i className="fa-solid fa-circle-check" style={{ color: '#fff', fontSize: '0.85rem' }} />
                                  ) : isRecordCreated ? (
                                    <i className="fa-solid fa-file-circle-plus" style={{ color: '#64748b', fontSize: '0.85rem' }} />
                                  ) : (
                                    <i className="fa-solid fa-pen" style={{ color: '#64748b', fontSize: '0.85rem' }} />
                                  )}
                                </div>

                                <div style={{ paddingTop: 2, paddingBottom: 24 }}>
                                  <div className="d-flex justify-content-between align-items-start mb-1">
                                    <div style={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.3 }}>
                                      {isStatusChange ? (
                                        <>
                                          <span>Status </span>
                                          <span className="text-muted fw-normal">changed to </span>
                                          <span>{statusValue}</span>
                                        </>
                                      ) : (
                                        <span>{entry.fieldLabel || entry.action}</span>
                                      )}
                                    </div>
                                    <div className="text-end ms-3" style={{ fontSize: '0.75rem', color: '#888', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                      <div className="fw-semibold">{date}</div>
                                      <div>{time}</div>
                                    </div>
                                  </div>

                                  <div className="mb-2" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                    {isRecordCreated ? `Initiated by ${entry.changedBy}` : `Changed by ${entry.changedBy}`}
                                  </div>

                                  {isRecordCreated ? (
                                    <div style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      background: '#f8fafc',
                                      border: '1px dashed #cbd5e1',
                                      borderRadius: 8,
                                      padding: '10px 14px',
                                      fontSize: '0.8rem',
                                      color: '#64748b',
                                    }}>
                                      <i className="fa-solid fa-shield-check" style={{ color: '#94a3b8' }} />
                                      Original field data entry completed.
                                    </div>
                                  ) : hasFromTo ? (
                                    <div className="d-flex gap-2">
                                      <div style={{
                                        flex: 1,
                                        background: '#f0f3ffff',
                                        borderRadius: 8,
                                        padding: '12px 16px',
                                      }}>
                                        <div style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: '#94a3b8', marginBottom: 6 }}>FROM</div>
                                        {isImageField && oldImages.length > 0 ? (
                                          <div className="d-flex flex-column gap-2">
                                            {oldImages.map((imgUrl, imgIdx) => (
                                              <a key={imgIdx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                                <img
                                                  src={imgUrl}
                                                  alt={`Previous ${imgIdx + 1}`}
                                                  className="img-fluid rounded"
                                                  style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }}
                                                  onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const parent = e.currentTarget.parentElement;
                                                    if (parent) {
                                                      const textDiv = document.createElement('div');
                                                      textDiv.style.fontSize = '0.8rem';
                                                      textDiv.style.color = '#ef4444';
                                                      textDiv.textContent = imgUrl;
                                                      parent.appendChild(textDiv);
                                                    }
                                                  }}
                                                />
                                              </a>
                                            ))}
                                          </div>
                                        ) : (
                                          <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                                            {entry.oldValue ?? '-'}
                                          </div>
                                        )}
                                      </div>
                                      <div style={{
                                        flex: 1,
                                        background: isStatusChange ? getStatusBgColor(statusValue) : '#f0f7ff',
                                        borderRadius: 8,
                                        padding: '12px 16px',
                                        border: isStatusChange ? 'none' : '1px solid #dbeafe',
                                      }}>
                                        <div style={{
                                          fontSize: '0.65rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.05em',
                                          fontWeight: 700,
                                          color: isStatusChange ? '#424242ff' : '#3b82f6',
                                          marginBottom: 6,
                                        }}>TO</div>
                                        {isImageField && newImages.length > 0 ? (
                                          <div className="d-flex flex-column gap-2">
                                            {newImages.map((imgUrl, imgIdx) => (
                                              <a key={imgIdx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                                <img
                                                  src={imgUrl}
                                                  alt={`New ${imgIdx + 1}`}
                                                  className="img-fluid rounded"
                                                  style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }}
                                                  onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const parent = e.currentTarget.parentElement;
                                                    if (parent) {
                                                      const textDiv = document.createElement('div');
                                                      textDiv.style.fontSize = '0.8rem';
                                                      textDiv.style.color = '#fff';
                                                      textDiv.textContent = imgUrl;
                                                      parent.appendChild(textDiv);
                                                    }
                                                  }}
                                                />
                                              </a>
                                            ))}
                                          </div>
                                        ) : (
                                          <div style={{ fontSize: '0.85rem', color: isStatusChange ? '#585858ff' : '#1d4ed8', fontWeight: 500 }}>
                                            {entry.newValue ?? '-'}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : null}

                                  {idx < activityHistory.length - 1 && (
                                    <hr style={{ margin: '16px 0 0 0', borderColor: '#f1f5f9' }} />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer d-flex justify-content-between bg-light">
              <button className="btn btn-secondary" onClick={() => onClose(false)}>
                <i className="fa fa-arrow-left me-1"></i> Back
              </button>

              <div className="d-flex gap-2">
                {canQc && status?.key === 3 && !isLoadingDecision && (
                  <>
                    <button
                      className="btn btn-outline-danger"
                      onClick={() => setShowRejectModal(true)}
                      disabled={isValidationFailed === true}
                      title={isValidationFailed === true ? "Belum bisa di-reject, masih ada validasi yang belum terpenuhi" : ""}
                    >
                      <i className="fa fa-times me-1"></i> Reject
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => setShowApproveModal(true)}
                      disabled={!retailPartnerRegistered || isApproveBlockedByValidation === true}
                      title={isApproveBlockedByValidation === true ? "Belum bisa di-approve, masih ada validasi yang belum terpenuhi" : (!retailPartnerRegistered ? "Retail partner belum terdaftar" : "")}
                    >
                      <i className="fa fa-check me-1"></i> Approve
                    </button>
                  </>
                )}
                {canQc && status?.key !== 3 && !isLoadingDecision && (
                  <button className="btn btn-warning" onClick={handleBackToQc}>
                    <i className="fa fa-undo me-1"></i> Back to QC
                  </button>
                )}
                {isLoadingDecision && <LoadingSpinner />}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showHistoryModal && selectedFieldHistory && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1070,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowHistoryModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto', padding: '0' }}>
                {selectedFieldHistory.history.length === 0 ? (
                  <div className="text-center text-muted py-4 small fst-italic">
                    Tidak ada riwayat perubahan
                  </div>
                ) : (
                  selectedFieldHistory.history.map((entry, idx) => {
                    const [dateStr, timeStr] = (entry.updatedAt || '').split(' ');
                    const isImageField = selectedFieldHistory.fieldType?.key === 3;

                    const splitImageUrls = (value: string | null | undefined): string[] => {
                      if (!value) return [];
                      return value.split(',').map(url => url.trim()).filter(url => url.length > 0);
                    };

                    const oldImages = splitImageUrls(entry.oldValue);
                    const newImages = splitImageUrls(entry.newValue);

                    return (
                      <div
                        key={idx}
                        style={{
                          padding: '20px 24px',
                          borderBottom: idx < selectedFieldHistory.history.length - 1 ? '1px solid #f0f0f0' : 'none',
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="fw-semibold" style={{ fontSize: '1rem' }}>{selectedFieldHistory.label}</span>
                          <div className="text-end" style={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap', marginLeft: 16 }}>
                            <div className="fw-bold">{dateStr}</div>
                            <div className="fw-semibold">{timeStr}</div>
                          </div>
                        </div>
                        <div className="text-muted mb-3" style={{ fontSize: '0.8rem' }}>
                          Changed by {entry.updatedBy}
                        </div>
                        <div className="d-flex gap-3">
                          <div
                            style={{
                              flex: 1,
                              background: '#f0f3ffff',
                              borderRadius: 8,
                              padding: '12px 16px',
                            }}
                          >
                            <div className="text-muted mb-2" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>FROM</div>
                            {isImageField && oldImages.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {oldImages.map((imgUrl, imgIdx) => (
                                  <a key={imgIdx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                    <img
                                      src={imgUrl}
                                      alt={`Previous ${imgIdx + 1}`}
                                      className="img-fluid rounded"
                                      style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const parent = e.currentTarget.parentElement;
                                        if (parent) {
                                          const textDiv = document.createElement('div');
                                          textDiv.style.fontSize = '0.8rem';
                                          textDiv.style.color = '#ef4444';
                                          textDiv.textContent = imgUrl;
                                          parent.appendChild(textDiv);
                                        }
                                      }}
                                    />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.875rem' }}>{entry.oldValue ?? '-'}</div>
                            )}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              background: '#f0f7ff',
                              borderRadius: 8,
                              padding: '12px 16px',
                              border: '1px solid #d0e8ff',
                            }}
                          >
                            <div className="mb-2" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, color: '#0d6efd' }}>TO</div>
                            {isImageField && newImages.length > 0 ? (
                              <div className="d-flex flex-column gap-2">
                                {newImages.map((imgUrl, imgIdx) => (
                                  <a key={imgIdx} href={imgUrl} target="_blank" rel="noreferrer" style={{ display: 'block' }}>
                                    <img
                                      src={imgUrl}
                                      alt={`New ${imgIdx + 1}`}
                                      className="img-fluid rounded"
                                      style={{ width: '100%', maxHeight: '150px', objectFit: 'contain', cursor: 'pointer' }}
                                      onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        const parent = e.currentTarget.parentElement;
                                        if (parent) {
                                          const textDiv = document.createElement('div');
                                          textDiv.style.fontSize = '0.8rem';
                                          textDiv.style.color = '#ef4444';
                                          textDiv.textContent = imgUrl;
                                          parent.appendChild(textDiv);
                                        }
                                      }}
                                    />
                                  </a>
                                ))}
                              </div>
                            ) : (
                              <div style={{ fontSize: '0.875rem' }}>{entry.newValue ?? '-'}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowRejectModal(false)}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Reject Notes</h5>
              </div>
              <div className="modal-body">
                <textarea
                  className="form-control"
                  placeholder="Write your reason here..."
                  value={rejectNotes}
                  onChange={(e) => setRejectNotes(e.target.value)}
                  rows={4}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-primary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                <button
                  className="btn btn-danger"
                  disabled={rejectNotes.trim() === ""}
                  onClick={() => {
                    handleDecision("reject", rejectNotes);
                    setShowRejectModal(false);
                  }}
                >
                  Confirm Reject
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showApproveModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => {
            setShowApproveModal(false);
            setEnableQcSelection(false);
            setSelectedQc("");
            setEnableApproveNotes(false);
            setApproveNotes("");
          }}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Notes</h5>
              </div>
              <div className="modal-body">
                <div className="mb-2 form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="enableQcSelection"
                    checked={enableQcSelection}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableQcSelection(checked);
                      if (!checked) {
                        setSelectedQc("");
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="enableQcSelection">
                    Assign to Advanced QC
                  </label>
                </div>

                {enableQcSelection && (
                  <div className="mb-3">
                    {isLoadingConfig ? (
                      <div className="py-2 text-center">
                        <LoadingSpinner />
                      </div>
                    ) : (
                      <MultiSelectDropdown
                        options={qcNameList.map(qc => ({
                          code: qc.id || qc.id?.toString() || '',
                          name: qc.name || '',
                        }))}
                        placeholder="Select Advanced QC..."
                        value={selectedQc ? [selectedQc] : []}
                        onChange={(selected) => setSelectedQc(selected[0] || '')}
                        single={true}
                        previewKey="name"
                      />
                    )}
                  </div>
                )}

                <div className="mb-2 form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="enableApproveNotes"
                    checked={enableApproveNotes}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setEnableApproveNotes(checked);
                      if (!checked) {
                        setApproveNotes("");
                      }
                    }}
                  />
                  <label className="form-check-label" htmlFor="enableApproveNotes">
                    Add approval notes
                  </label>
                </div>

                <textarea
                  className="form-control"
                  placeholder="Write your approval notes here..."
                  value={approveNotes}
                  onChange={(e) => setApproveNotes(e.target.value)}
                  rows={4}
                  disabled={!enableApproveNotes}
                />
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline-primary" onClick={() => {
                  setShowApproveModal(false);
                  setEnableQcSelection(false);
                  setSelectedQc("");
                  setEnableApproveNotes(false);
                  setApproveNotes("");
                }}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowApproveModal(false);
                    if (enableQcSelection && selectedQc) {
                      handleDecision("approveToQcField", approveNotes, selectedQc);
                    } else {
                      handleDecision("approve", approveNotes);
                    }
                    setEnableQcSelection(false);
                    setSelectedQc("");
                    setEnableApproveNotes(false);
                    setApproveNotes("");
                  }}
                >
                  Confirm Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUpdateValueModal && selectedResultValue && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => {
            if (!isLoadingUpdateValue) {
              setShowUpdateValueModal(false);
            }
          }}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Value</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    if (!isLoadingUpdateValue) {
                      setShowUpdateValueModal(false);
                    }
                  }}
                  disabled={isLoadingUpdateValue}
                />
              </div>
              <div className="modal-body">
                <p className="fw-bold mb-3">
                  {selectedResultValue.label}
                </p>

                {(() => {
                  const fieldTypeKey = selectedResultValue.fieldType?.key ?? 0;
                  const hasOptions = selectedResultValue.options && selectedResultValue.options.length > 0;

                  if (fieldTypeKey === 6 && hasOptions) {
                    return (
                      <div className="mb-3">
                        <MultiSelectDropdown
                          options={multiSelectOptions}
                          value={selectedOptions}
                          onChange={handleMultipleSelectionChange}
                          placeholder="Pilih opsi..."
                          previewKey="name"
                        />
                      </div>
                    );
                  }

                  if (fieldTypeKey === 5 && hasOptions) {
                    return (
                      <div className="mb-3">
                        <MultiSelectDropdown
                          options={selectedResultValue.options!.map(opt => ({
                            code: opt,
                            name: opt
                          }))}
                          placeholder="Pilih opsi..."
                          value={selectedResultValue.value ? [selectedResultValue.value] : []}
                          onChange={(selected) => handleSingleSelectionChange(selected[0] || '')}
                          single={true}
                          isSourceModal={true}
                          disabled={isEmptyField}
                        />
                      </div>
                    );
                  }

                  return (
                    <textarea
                      className="form-control"
                      placeholder="Update value here..."
                      value={selectedResultValue.value}
                      onChange={(e) => setSelectedResultValue({ ...selectedResultValue, value: e.target.value })}
                      rows={4}
                      disabled={isLoadingUpdateValue}
                    />
                  );
                })()}

                {(() => {
                  const fieldTypeKey = selectedResultValue.fieldType?.key ?? 0;
                  const hasOptions = selectedResultValue.options && selectedResultValue.options.length > 0;

                  if (fieldTypeKey === 5 && hasOptions) {
                    return (
                      <div className="mb-3 form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="empty-field"
                          id="empty-field"
                          checked={isEmptyField}
                          onChange={(e) => setIsEmptyField(e.target.checked)}
                        />
                        <label htmlFor="empty-field" className="form-check-label">
                          Kosongkan field
                        </label>
                      </div>
                    );
                  }

                  return null;
                })()}


              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => {
                    setShowUpdateValueModal(false);
                    setIsEmptyField(false);
                  }}
                  disabled={isLoadingUpdateValue}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={isLoadingUpdateValue}
                  onClick={() => {
                    let finalValue = selectedResultValue.value;
                    const fieldTypeKey = selectedResultValue.fieldType?.key ?? 0;

                    if (isEmptyField && fieldTypeKey === 5) {
                      finalValue = "";
                    } else if (fieldTypeKey === 6 && selectedResultValue.options && selectedResultValue.options.length > 0) {
                      finalValue = selectedOptions.join(', ');
                    }

                    handleSubmitUpdateValue(selectedResultValue.id, finalValue, item?.id, selectedResultValue.fieldId);
                  }}
                >
                  {isLoadingUpdateValue ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    'Update Value'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUpdateFile && selectedResultValue && (() => {
        const hasCropRatio = Boolean(
          selectedResultValue.availableRatioCameraInApps &&
          selectedResultValue.availableRatioCameraInApps.trim() !== ""
        );
        const isCropping = hasCropRatio && enableCrop;

        return (
          <div
            className="modal d-block"
            tabIndex={-1}
            role="dialog"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              position: "fixed",
              inset: 0,
              zIndex: 1060,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
            onClick={() => setShowUpdateFile(false)}
          >
            <div
              className="modal-dialog"
              role="document"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Update Image</h5>
                </div>

                <div className="modal-body">
                  <p className="mb-2 fw-bold">{selectedResultValue.label}</p>

                  {/* Enable Crop checkbox: shown if field has ratio configuration */}
                  {hasCropRatio && (
                    <div className="mb-3 form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="enableCrop"
                        checked={enableCrop}
                        onChange={(e) => setEnableCrop(e.target.checked)}
                        style={{ cursor: "pointer" }}
                      />
                      <label
                        className="form-check-label user-select-none"
                        htmlFor="enableCrop"
                        style={{ cursor: "pointer" }}
                      >
                        Enable Crop
                      </label>
                    </div>
                  )}

                  {!previewUrl && (
                    <div
                      {...getRootProps()}
                      className={`border rounded p-4 text-center cursor-pointer ${isDragActive ? "border-primary bg-light" : "border-secondary"}`}
                      style={{ cursor: "pointer" }}
                    >
                      <input {...getInputProps()} />
                      <p className="mb-0 text-muted">
                        Drag & drop image here, or click to select
                      </p>
                    </div>
                  )}

                  {previewUrl && (
                    isCropping ? (
                      <>
                        <div
                          style={{
                            position: "relative",
                            width: "100%",
                            height: 350,
                            background: "#1a1a2e",
                            borderRadius: 8,
                            overflow: "hidden",
                          }}
                        >
                          <Cropper
                            image={previewUrl}
                            crop={crop}
                            zoom={zoom}
                            aspect={cropAspect}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_croppedArea, croppedAreaPixels) => {
                              setCroppedAreaPixels(croppedAreaPixels);
                            }}
                            style={{
                              cropAreaStyle: {
                                border: "2px solid #0d6efd",
                                boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
                              },
                            }}
                          />
                        </div>

                        {(() => {
                          const rawRatios = (selectedResultValue.availableRatioCameraInApps || "")
                            .split(',')
                            .map(s => s.trim().toLowerCase())
                            .filter(Boolean);

                          const ratioLabels: Record<string, string> = {
                            portrait916: 'Portrait (9:16)',
                            portrait: 'Portrait (9:16)',
                            portrait34: 'Portrait (3:4)',
                            square: 'Square (1:1)',
                            landscape43: 'Landscape (4:3)',
                            landscape169: 'Landscape (16:9)',
                            landscape: 'Landscape (16:9)',
                          };

                          // Filter out legacy aliases if their new counterpart exists to avoid duplicate buttons
                          const ratios = rawRatios.filter(r => {
                            if (r === 'portrait' && rawRatios.includes('portrait916')) return false;
                            if (r === 'landscape' && rawRatios.includes('landscape169')) return false;
                            return true;
                          });

                          return ratios.length > 1 ? (
                            <div className="mt-3 d-flex align-items-center gap-2">
                              <span className="fw-bold small text-muted">Ratio:</span>
                              {ratios.map((ratio) => {
                                const val = getRatioValue(ratio);
                                const label = ratioLabels[ratio] || (ratio.charAt(0).toUpperCase() + ratio.slice(1));
                                const isActive = cropAspect === val;
                                return (
                                  <button
                                    key={ratio}
                                    type="button"
                                    className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    onClick={() => setCropAspect(val)}
                                  >
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          ) : null;
                        })()}

                        {/* Zoom Slider */}
                        <div className="mt-3 d-flex align-items-center gap-2">
                          <span className="fw-bold small text-muted">Zoom:</span>
                          <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="form-range flex-grow-1"
                            style={{ accentColor: "#0d6efd" }}
                          />
                        </div>

                        {/* Change image link */}
                        <div className="mt-2 text-end">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-muted p-0"
                            onClick={() => {
                              setSelectedFile(null);
                              setPreviewUrl(null);
                              setCroppedAreaPixels(null);
                              setCrop({ x: 0, y: 0 });
                              setZoom(1);
                            }}
                          >
                            <i className="fa-solid fa-rotate-left me-1" />
                            Ganti Gambar
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="mt-3 text-center">
                        <img
                          src={previewUrl}
                          alt="Preview"
                          className="img-fluid rounded"
                          style={{ maxHeight: "300px", objectFit: "contain" }}
                        />
                        <div className="mt-2 text-end">
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-muted p-0"
                            onClick={() => {
                              setSelectedFile(null);
                              setPreviewUrl(null);
                              setCroppedAreaPixels(null);
                            }}
                          >
                            <i className="fa-solid fa-rotate-left me-1" />
                            Ganti Gambar
                          </button>
                        </div>
                      </div>
                    )
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowUpdateFile(false);
                      setSelectedFile(null);
                      setPreviewUrl(null);
                      setCroppedAreaPixels(null);
                      setCrop({ x: 0, y: 0 });
                      setZoom(1);
                      setEnableCrop(true);
                    }}
                    disabled={isLoadingUpdateValue}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!selectedFile || isLoadingUpdateValue}
                    onClick={handleSubmitUpdateFile}
                  >
                    {isLoadingUpdateValue
                      ? "Uploading..."
                      : isCropping
                        ? "Crop & Upload"
                        : "Upload"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {showUpdateLocation && oldLat && oldLng && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "fixed",
            inset: 0,
            zIndex: 1060,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={() => setShowUpdateLocation(false)}
        >
          <div
            className="modal-dialog modal-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 modal-content">
              <h5 className="mb-3">Update Location Coordinate</h5>

              <div className="row">
                <div className="col-md-5">
                  <p>Result ID<br /><b>{id}</b></p>
                  <p>Submitted At<br /><b>{submittedAt}</b></p>
                  <p>Name<br /><b>{taskName}</b></p>
                  <p>Description<br /><b>{description}</b></p>
                  <p>Field User<br />
                    <b>{installerName}</b>
                  </p>
                </div>
                <div className='col-md-7'>
                  <div className='mb-3 col-md-12'>
                    <div className="mb-3 d-flex align-items-center col-md-12">
                      <div>
                        <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M23.1436 45.757L3.13397 25.731L42.7225 25.3101L23.1436 45.757Z" fill="#42A5F5" />
                          <ellipse cx="22.8865" cy="20.4632" rx="20.4736" ry="20.4632" fill="#42A5F5" />
                          <ellipse cx="22.8865" cy="20.4632" rx="9.09937" ry="9.09476" fill="black" fill-opacity="0.2" />
                        </svg>
                      </div>
                      <div className='mx-4'>
                        <label className="fw-semibold">Old Coordinate</label>
                        <input
                          className="mb-2 form-control"
                          disabled
                          value={`${oldLat}, ${oldLng}`}
                        />
                      </div>
                    </div>
                    <div className="mb-3 d-flex align-items-center col-md-12">
                      <div>
                        <svg width="46" height="46" viewBox="0 0 46 46" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M23.1436 45.757L3.13397 25.731L42.7225 25.3101L23.1436 45.757Z" fill="#20D5F1" />
                          <ellipse cx="22.8865" cy="20.4632" rx="20.4736" ry="20.4632" fill="#20D5F1" />
                          <ellipse cx="22.8865" cy="20.4632" rx="9.09937" ry="9.09476" fill="black" fill-opacity="0.2" />
                        </svg>
                      </div>
                      <div className='mx-4'>
                        <label className="fw-semibold">New Coordinate</label>
                        <input
                          className="form-control"
                          value={`${newLat}, ${newLng}`}
                          onChange={(e) => {
                            const [lat, lng] = e.target.value.split(",").map(Number);
                            if (!isNaN(lat) && !isNaN(lng)) {
                              setNewLat(lat);
                              setNewLng(lng);
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="mb-3 col-md-12">
                      <label className="fw-semibold mb-1">Address</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Enter address"
                        value={newAddress}
                        onChange={(e) => setNewAddress(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <Map
                      ref={locationMapRef}
                      mapboxAccessToken={MAPBOX_TOKEN}
                      initialViewState={{
                        longitude: oldLng,
                        latitude: oldLat,
                        zoom: 16,
                      }}
                      style={{ width: '100%', height: 300 }}
                      mapStyle="mapbox://styles/mapbox/streets-v12"
                    >
                      <Marker
                        longitude={oldLng}
                        latitude={oldLat}
                        anchor="bottom"
                      >
                        <img
                          src="/assets/markers/icon-marker.svg"
                          style={{ width: 25, height: 25 }}
                          alt="Old location"
                        />
                      </Marker>

                      {newLat && newLng && (
                        <Marker
                          longitude={newLng}
                          latitude={newLat}
                          anchor="bottom"
                          draggable
                          onDragEnd={(e) => {
                            setNewLat(e.lngLat.lat)
                            setNewLng(e.lngLat.lng)
                          }}
                        >
                          <img
                            src="/assets/markers/icon-marker-selected.svg"
                            style={{ width: 25, height: 25, cursor: 'grab' }}
                            alt="New location"
                          />
                        </Marker>
                      )}

                      {newLat && newLng && (
                        <Source
                          id="location-line"
                          type="geojson"
                          data={{
                            type: 'FeatureCollection',
                            features: [{
                              type: 'Feature',
                              geometry: {
                                type: 'LineString',
                                coordinates: [
                                  [oldLng, oldLat],
                                  [newLng, newLat],
                                ],
                              },
                              properties: {},
                            }],
                          }}
                        >
                          <Layer
                            id="location-line-layer"
                            type="line"
                            paint={{
                              'line-color': '#1e88e5',
                              'line-width': 3,
                              'line-opacity': 0.8,
                            }}
                          />
                        </Source>
                      )}
                    </Map>

                    <div className="mt-2 fw-semibold">
                      Distance Gap: {distanceGap}m
                    </div>
                  </div>
                </div>
              </div>

              <div className="gap-2 mt-4 d-flex justify-content-end">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setShowUpdateLocation(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    const token = profile?.data.token;
                    if (!token || !newLat || !newLng) return;

                    await dashboardDataService.dashboardDataUpdateResultLocation(
                      token,
                      id,
                      newLat,
                      newLng,
                      newAddress
                    );

                    setShowUpdateLocation(false);
                    fetchDetail();
                  }}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNoteModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => !isLoadingUpdateNote && setShowNoteModal(false)}
        >
          <div className="modal-dialog" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{isNoteReadOnly ? 'View Note' : notes.length > 0 ? 'Edit Note' : 'Add Note'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => !isLoadingUpdateNote && setShowNoteModal(false)}
                  disabled={isLoadingUpdateNote}
                />
              </div>
              <div className="modal-body">
                <textarea
                  className="form-control"
                  placeholder={isNoteReadOnly ? 'Tidak ada catatan' : 'tambahkan catatan disini...'}
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  rows={4}
                  disabled={isNoteReadOnly || isLoadingUpdateNote}
                />
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setShowNoteModal(false)}
                  disabled={isLoadingUpdateNote}
                >
                  {isNoteReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isNoteReadOnly && (
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmitUpdateNote}
                    disabled={isLoadingUpdateNote || noteInput === notes}
                  >
                    {isLoadingUpdateNote ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showConsentModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowConsentModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <i className="fa-solid fa-circle-check text-primary me-2 fs-5"></i>
                  Detail Konfirmasi Pengecekan Toko Sekitar
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowConsentModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {selectedConsent ? (
                  <div>
                    <div className="row mb-3">
                      <div className="col-md-6 mb-3">
                        <label className="text-muted small d-block mb-1">Status Konfirmasi</label>
                        <span className="badge bg-success-subtle text-success border border-success-subtle px-2 py-1 fs-6">
                          <i className="fa-solid fa-check me-1"></i> Disetujui (Confirmed)
                        </span>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="text-muted small d-block mb-1">Kode Consent</label>
                        <span className="fw-semibold text-dark fs-6">{selectedConsent.consentCode}</span>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="text-muted small d-block mb-1">Waktu Konfirmasi</label>
                        <span className="fw-semibold text-dark">{selectedConsent.consentAt || selectedConsent.createdAt || '-'}</span>
                      </div>
                    </div>

                    {selectedConsent.attachmentUrl ? (
                      <div className="mt-3">
                        <label className="text-muted small d-block mb-2 fw-semibold">Bukti Screenshot Konfirmasi (Attachment)</label>
                        <div>
                          <a href={selectedConsent.attachmentUrl} target="_blank" rel="noreferrer">
                            <ImageWithLoading
                              src={selectedConsent.attachmentUrl}
                              alt="Consent Attachment"
                              className="border rounded"
                              style={{ width: "100%", height: "auto", display: "block" }}
                            />
                          </a>
                        </div>
                      </div>
                    ) : (
                      <div className="alert alert-info small mt-3">
                        Tidak ada foto lampiran (attachment) untuk konfirmasi ini.
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-muted mb-0">Data consent tidak ditemukan.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConsentModal(false)}>
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMissRoutingModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          role="dialog"
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            position: 'fixed',
            inset: 0,
            zIndex: 1060,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => setShowMissRoutingModal(false)}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center text-danger">
                  <i className="fa-solid fa-triangle-exclamation text-danger me-2 fs-5"></i>
                  Detail Ketidaksesuaian Rute Pengisian
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowMissRoutingModal(false)}
                ></button>
              </div>
              <div className="modal-body p-4" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {canOverrideMissRouting && (
                  <div className="mb-3 p-3 bg-light rounded border border-warning-subtle">
                    <div className="d-flex align-items-center gap-3">
                      <div className="form-check form-switch m-0 p-0 flex-shrink-0">
                        <input
                          className="form-check-input m-0"
                          type="checkbox"
                          role="switch"
                          id="toggleMissRoutingRestriction"
                          checked={!isRestrictMissRouting}
                          onChange={handleToggleMissRoutingRestriction}
                          disabled={isUpdatingRestriction}
                          style={{ cursor: 'pointer', width: '2.5em', height: '1.25em' }}
                        />
                      </div>
                      <div>
                        <label
                          className="form-check-label fw-bold text-dark d-block mb-1"
                          htmlFor="toggleMissRoutingRestriction"
                          style={{ cursor: 'pointer' }}
                        >
                          Tetap Izinkan Approve/Reject Meski Ada Ketidaksesuaian
                        </label>
                        <div className="form-text text-muted small mb-0">
                          Aktifkan ini kalau kamu tetap ingin bisa approve atau reject data, walaupun ditemukan ketidaksesuaian pada rute pengisian.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!isRestrictMissRouting && (
                  <div className="alert alert-info py-2 px-3 mb-3 small d-flex align-items-center">
                    <i className="fa-solid fa-circle-info me-2 text-info fs-6"></i>
                    <span>Pembatasan untuk data ini sudah dinonaktifkan, jadi kamu tetap bisa menyetujui atau menolak seperti biasa.</span>
                  </div>
                )}
                <p className="text-muted small mb-3">
                  Ini beberapa bagian yang perlu dicek karena tidak sesuai dengan alur pengisian form:
                </p>
                <div className="list-group">
                  {actionRuleValidationDetails.map((detail, idx) => (
                    <div key={idx} className="list-group-item list-group-item-action flex-column align-items-start p-3 border-danger-subtle mb-2 rounded border">
                      <div className="d-flex w-100 justify-content-between align-items-center mb-1">
                        <h6 className="mb-1 text-danger fw-semibold">{detail.ruleName}</h6>
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle">{detail.ruleType}</span>
                      </div>
                      <p className="mb-1 text-dark small fw-medium">{detail.errorMessage || detail.ruleName}</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setShowMissRoutingModal(false)}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardDetailModal;